/**
 * Platform admin tab for managing accounts.
 * Allows creating, editing, and deleting accounts.
 * Team management is handled at the Account Settings level, not here.
 */
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";

interface Account {
	id: string;
	name: string;
	slug: string;
	settings: {
		timezone?: string;
		dateFormat?: string;
		currency?: string;
	} | null;
	createdAt: Date;
	updatedAt: Date;
	teamCount: number;
	userCount: number;
}

/**
 * Generates a URL-safe slug from a name.
 * @param name - the name to convert to a slug
 */
function generateSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");
}

/**
 * Accounts management tab
 * Lists all accounts. Team management is handled at the Account Settings level.
 */
export function AccountsTab() {
	// Dialog states
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [editingAccount, setEditingAccount] = useState<Account | null>(null);
	const [deletingAccount, setDeletingAccount] = useState<Account | null>(null);

	// Create form state
	const [newName, setNewName] = useState("");
	const [newSlug, setNewSlug] = useState("");
	const [newSlugManuallyEdited, setNewSlugManuallyEdited] = useState(false);

	// Edit form state
	const [editName, setEditName] = useState("");

	const utils = trpc.useUtils();

	// Queries
	const accountsQuery = trpc.accounts.list.useQuery();

	// Mutations
	const createMutation = trpc.accounts.create.useMutation({
		onSuccess: () => {
			void utils.accounts.list.invalidate();
			setCreateDialogOpen(false);
			resetCreateForm();
		},
	});

	const updateMutation = trpc.accounts.update.useMutation({
		onSuccess: () => {
			void utils.accounts.list.invalidate();
			setEditingAccount(null);
		},
	});

	const deleteMutation = trpc.accounts.delete.useMutation({
		onSuccess: () => {
			void utils.accounts.list.invalidate();
			setDeletingAccount(null);
		},
	});

	/**
	 * Resets the create account form to initial state
	 */
	const resetCreateForm = () => {
		setNewName("");
		setNewSlug("");
		setNewSlugManuallyEdited(false);
	};

	/**
	 * Handles account name change and auto-generates slug
	 * @param name - the new account name
	 */
	const handleNameChange = (name: string) => {
		setNewName(name);
		if (!newSlugManuallyEdited) {
			setNewSlug(generateSlug(name));
		}
	};

	/**
	 * Handles account slug change and marks it as manually edited
	 * @param slug - the new account slug
	 */
	const handleSlugChange = (slug: string) => {
		setNewSlug(slug);
		setNewSlugManuallyEdited(true);
	};

	/**
	 * Creates a new account with the form values
	 */
	const handleCreate = () => {
		createMutation.mutate({
			name: newName,
			slug: newSlug,
		});
	};

	/**
	 * Opens the edit dialog for an account
	 * @param account - the account to edit
	 */
	const handleEdit = (account: Account) => {
		setEditingAccount(account);
		setEditName(account.name);
	};

	/**
	 * Updates the currently editing account
	 */
	const handleUpdate = () => {
		if (!editingAccount) return;
		updateMutation.mutate({
			id: editingAccount.id,
			name: editName,
		});
	};

	/**
	 * Deletes the account marked for deletion
	 */
	const handleDelete = () => {
		if (!deletingAccount) return;
		deleteMutation.mutate({ id: deletingAccount.id });
	};

	/**
	 * Formats a date for display
	 * @param date - the date to format
	 */
	const formatDate = (date: Date) => {
		return new Date(date).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-lg font-semibold">Accounts</h2>
				<Button onClick={() => { setCreateDialogOpen(true); }}>Add Account</Button>
			</div>

			<div className="rounded-lg border">
				{accountsQuery.isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading accounts...</div>
				) : accountsQuery.error ? (
					<div className="p-8 text-center text-destructive">
						Error loading accounts: {accountsQuery.error.message}
					</div>
				) : accountsQuery.data?.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">No accounts found.</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Slug</TableHead>
								<TableHead>Teams</TableHead>
								<TableHead>Users</TableHead>
								<TableHead>Created</TableHead>
								<TableHead className="w-[150px]">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{accountsQuery.data?.map((account) => (
								<TableRow key={account.id}>
									<TableCell className="font-medium">{account.name}</TableCell>
									<TableCell className="font-mono text-sm">{account.slug}</TableCell>
									<TableCell>{account.teamCount}</TableCell>
									<TableCell>{account.userCount}</TableCell>
									<TableCell>{formatDate(account.createdAt)}</TableCell>
									<TableCell>
										<div className="flex gap-2">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => { handleEdit(account); }}
											>
												Edit
											</Button>
											<Button
												variant="ghost"
												size="sm"
												className="text-destructive hover:text-destructive"
												onClick={() => { setDeletingAccount(account); }}
											>
												Delete
											</Button>
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Create Account Dialog */}
			<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Account</DialogTitle>
						<DialogDescription>
							Create a new client account. Teams can be managed in Account Settings.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="name">Account Name</Label>
								<Input
									id="name"
									placeholder="Acme Corporation"
									value={newName}
									onChange={(e) => { handleNameChange(e.target.value); }}
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="slug">Slug</Label>
								<Input
									id="slug"
									placeholder="acme-corp"
									value={newSlug}
									onChange={(e) => { handleSlugChange(e.target.value); }}
								/>
								<p className="text-xs text-muted-foreground">
									URL-safe identifier. Lowercase letters, numbers, and hyphens only.
								</p>
							</div>
						</div>
						{createMutation.error && (
							<p className="text-sm text-destructive">{createMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button
								variant="outline"
								type="button"
								onClick={() => {
									setCreateDialogOpen(false);
									resetCreateForm();
								}}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={!newName || !newSlug || createMutation.isPending}
							>
								{createMutation.isPending ? "Creating..." : "Create Account"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Edit Account Dialog */}
			<Dialog open={editingAccount !== null} onOpenChange={(open) => { if (!open) setEditingAccount(null); }}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Account</DialogTitle>
						<DialogDescription>
							Update account details. Slug cannot be changed.
						</DialogDescription>
					</DialogHeader>
					<form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label>Slug</Label>
								<Input value={editingAccount?.slug ?? ""} disabled />
							</div>
							<div className="grid gap-2">
								<Label htmlFor="edit-name">Account Name</Label>
								<Input
									id="edit-name"
									value={editName}
									onChange={(e) => { setEditName(e.target.value); }}
								/>
							</div>
						</div>
						{updateMutation.error && (
							<p className="text-sm text-destructive">{updateMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button variant="outline" type="button" onClick={() => { setEditingAccount(null); }}>
								Cancel
							</Button>
							<Button type="submit" disabled={updateMutation.isPending}>
								{updateMutation.isPending ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Account Dialog */}
			<Dialog open={deletingAccount !== null} onOpenChange={(open) => { if (!open) setDeletingAccount(null); }}>
				<DialogContent>
					<form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
						<DialogHeader>
							<DialogTitle>Delete Account</DialogTitle>
							<DialogDescription>
								Are you sure you want to delete &quot;{deletingAccount?.name}&quot;? This will delete all
								teams and user access associated with this account. This action cannot be undone.
							</DialogDescription>
						</DialogHeader>
						{deleteMutation.error && (
							<p className="text-sm text-destructive">{deleteMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button type="button" variant="outline" onClick={() => { setDeletingAccount(null); }}>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="destructive"
								disabled={deleteMutation.isPending}
							>
								{deleteMutation.isPending ? "Deleting..." : "Delete Account"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
